import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useGuardiesAra, type TecnicGuardiaDto } from '@/api/activacions';
import { Icona } from '@/components/Icona';
import { useFormat } from '@/i18n/useFormat';
import { useMe } from '@/permisos/MeContext';
import type { AreaEstructura, DepartamentEstructura } from '../marc/estructura';
import { PersonaGuardia } from './comu';

const RUTA_CALENDARIS = '/planificacio/calendaris';

/** Guàrdies d'ara, si l'usuari les pot consultar. */
function useGuardies() {
  const { pot } = useMe();
  const permis = pot('cai.guardies', 'CONSULTAR');
  const q = useGuardiesAra(permis);
  return { permis, ...q };
}

function CapGuardia({ extra, calendaris }: { extra?: ReactNode; calendaris?: boolean }) {
  const { t } = useTranslation('administracio');
  return (
    <div className="panel__head">
      <i className="dot dot--green" aria-hidden="true" />
      <h3>{t('estructura.guardia.titol')}</h3>
      {extra}
      <div className="spacer" />
      <Link to={RUTA_CALENDARIS} className="btn btn--ghost btn--xs">
        {calendaris ? t('estructura.guardia.calendaris') : t('estructura.guardia.calendari')}
        <Icona nom="chevron-right" />
      </Link>
    </div>
  );
}

/** Titular i suplent d'un tècnic de guàrdia. */
function Tecnic({ tecnic }: { tecnic: TecnicGuardiaDto }) {
  const { t } = useTranslation('administracio');
  const f = useFormat();
  return (
    <div className="est-guardia__persones">
      <PersonaGuardia
        nom={tecnic.titularNom ?? '—'}
        sub={
          <>
            {t('estructura.guardia.titular')}
            {tecnic.fins
              ? ` · ${t('estructura.guardia.fins', { data: f.dataHora(tecnic.fins) })}`
              : ''}
          </>
        }
      />
      {tecnic.substitutNom ? (
        <PersonaGuardia nom={tecnic.substitutNom} sub={t('estructura.guardia.suplent')} suplent />
      ) : (
        <span className="badge badge--incident">{t('estructura.guardia.senseSuplent')}</span>
      )}
    </div>
  );
}

/** «De guàrdia ara» d'una àrea: titular, suplent i fins quan; avís si no hi ha ningú. */
export function GuardiaArea({ area }: { area: AreaEstructura }) {
  const { t } = useTranslation('administracio');
  const g = useGuardies();
  if (!g.permis) return null;
  const dades = g.data?.departaments.flatMap((d) => d.arees).find((a) => a.id === area.id);
  const tecnic = dades?.tecnic?.titularNom ? dades.tecnic : null;
  const crisi = area.tipus === 'CRISI';
  return (
    <div className="panel est-guardia" data-guardia-area={area.id}>
      <CapGuardia
        extra={
          <span className="t-caption-2 dim">
            {t('estructura.guardia.cobreix', { count: area.serveis.length })}
          </span>
        }
      />
      <div className="panel__body">
        {g.isLoading ? (
          <p className="dim t-footnote est-guardia__text">{t('estructura.guardia.carregant')}</p>
        ) : g.isError ? (
          <p className="dim t-footnote est-guardia__text">{t('estructura.guardia.senseDades')}</p>
        ) : tecnic ? (
          <Tecnic tecnic={tecnic} />
        ) : crisi ? (
          <p className="dim t-footnote est-guardia__text">{t('estructura.guardia.crisi')}</p>
        ) : (
          <div className="est-guardia__alerta">
            <span className="badge badge--incident">
              <Icona nom="exclamationmark-triangle" />
              {t('estructura.guardia.ningu')}
            </span>
            <span className="dim t-footnote">{t('estructura.guardia.ninguText')}</span>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * «De guàrdia ara» d'un departament: guàrdia del departament (si n'hi ha) i taula de les
 * seves àrees amb titular i suplent, marcant les que no tenen cobertura.
 */
export function GuardiaDepartament({ departament }: { departament: DepartamentEstructura }) {
  const { t } = useTranslation('administracio');
  const f = useFormat();
  const g = useGuardies();
  if (!g.permis) return null;
  const dades = g.data?.departaments.find((d) => d.id === departament.id);
  const arees = departament.arees.filter((a) => a.actiu !== false);
  const tecnicDe = (id: number) => {
    const tc = dades?.arees.find((a) => a.id === id)?.tecnic;
    return tc?.titularNom ? tc : null;
  };
  const sense = arees.filter((a) => a.tipus === 'GUARDIA_IT' && !tecnicDe(a.id)).length;
  const depTecnic = dades?.tecnic?.titularNom ? dades.tecnic : null;
  return (
    <div className="panel est-guardia" data-guardia-departament={departament.id}>
      <CapGuardia
        calendaris
        extra={
          g.data && arees.length > 0 ? (
            sense > 0 ? (
              <span className="badge badge--incident">
                {t('estructura.guardia.areesSense', { count: sense })}
              </span>
            ) : (
              <span className="badge badge--ok">{t('estructura.guardia.totesCobertes')}</span>
            )
          ) : undefined
        }
      />
      {g.isLoading ? (
        <div className="panel__body">
          <p className="dim t-footnote est-guardia__text">{t('estructura.guardia.carregant')}</p>
        </div>
      ) : g.isError ? (
        <div className="panel__body">
          <p className="dim t-footnote est-guardia__text">{t('estructura.guardia.senseDades')}</p>
        </div>
      ) : (
        <>
          <div className="panel__body est-guardia__dep">
            <span className="est-guardia__etiqueta">{t('estructura.guardia.departament')}</span>
            {depTecnic ? (
              <PersonaGuardia
                nom={depTecnic.titularNom ?? '—'}
                sub={
                  <>
                    {depTecnic.fins
                      ? `${t('estructura.guardia.fins', { data: f.dataHora(depTecnic.fins) })} · `
                      : ''}
                    {t('estructura.guardia.departamentText')}
                  </>
                }
              />
            ) : (
              <span className="dim t-footnote">{t('estructura.guardia.senseDepartament')}</span>
            )}
          </div>
          {arees.length === 0 ? (
            <div className="panel__body">
              <p className="dim t-footnote est-guardia__text">
                {t('estructura.guardia.senseArees')}
              </p>
            </div>
          ) : (
            <div className="est-taula-scroll">
              <table className="taula">
                <thead>
                  <tr>
                    <th>{t('estructura.guardia.area')}</th>
                    <th>{t('estructura.guardia.titular')}</th>
                    <th>{t('estructura.guardia.suplent')}</th>
                  </tr>
                </thead>
                <tbody>
                  {arees.map((a) => {
                    const tc = tecnicDe(a.id);
                    return (
                      <tr key={a.id} data-fila-guardia={a.id}>
                        <td>
                          <span className="hstack gap-6">
                            <span
                              className={`node-punt node-punt--${a.tipus.toLowerCase()}`}
                              aria-hidden="true"
                            />
                            <span className="strong">{a.nom}</span>
                          </span>
                        </td>
                        {tc ? (
                          <>
                            <td>
                              <PersonaGuardia
                                nom={tc.titularNom ?? '—'}
                                sub={
                                  tc.fins
                                    ? t('estructura.guardia.fins', { data: f.dataHora(tc.fins) })
                                    : undefined
                                }
                              />
                            </td>
                            <td>
                              {tc.substitutNom ? (
                                <PersonaGuardia nom={tc.substitutNom} suplent />
                              ) : (
                                <span className="badge badge--incident">
                                  {t('estructura.guardia.senseSuplent')}
                                </span>
                              )}
                            </td>
                          </>
                        ) : (
                          <td colSpan={2}>
                            {a.tipus === 'CRISI' ? (
                              <span className="dim t-footnote">
                                {t('estructura.guardia.crisiFila')}
                              </span>
                            ) : (
                              <span className="badge badge--incident">
                                <Icona nom="exclamationmark-triangle" />
                                {t('estructura.guardia.senseCobertura')}
                              </span>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/** Guàrdia que cobreix un servei: la de la seva àrea. */
export function GuardiaServei({ area }: { area: AreaEstructura }) {
  const { t } = useTranslation('administracio');
  const g = useGuardies();
  if (!g.permis) return null;
  const tecnic = g.data?.departaments.flatMap((d) => d.arees).find((a) => a.id === area.id)?.tecnic;
  const crisi = area.tipus === 'CRISI';
  return (
    <div className="panel est-guardia">
      <CapGuardia
        extra={
          <span className="t-caption-2 dim">
            {t('estructura.guardia.serveiText', { area: area.nom })}
          </span>
        }
      />
      <div className="panel__body">
        {g.isLoading ? (
          <p className="dim t-footnote est-guardia__text">{t('estructura.guardia.carregant')}</p>
        ) : tecnic?.titularNom ? (
          <Tecnic tecnic={tecnic} />
        ) : crisi ? (
          <p className="dim t-footnote est-guardia__text">{t('estructura.guardia.crisi')}</p>
        ) : (
          <div className="est-guardia__alerta">
            <span className="badge badge--incident">
              <Icona nom="exclamationmark-triangle" />
              {t('estructura.guardia.ningu')}
            </span>
            <span className="dim t-footnote">{t('estructura.guardia.ninguText')}</span>
          </div>
        )}
      </div>
    </div>
  );
}
